/**
 * Pure helpers for the `status_matrix` widget.
 *
 * The widget reads a pre-aggregated matrix off a SETTING record's JSON `value`,
 * written by the user's own flow. The matrix is generic: groups of rows ("items"),
 * each with a list of status cells. This module holds that value shape plus the
 * pure status → appearance logic, so it can be unit-tested without a DOM or a
 * network round-trip.
 */

/** A link target for a cell: a record (by type + id) or an absolute/relative URL. */
export interface MatrixCellLink {
    /** Record type, e.g. `execution`, `flow`, `custom_object`. */
    record_type?: string;
    /** Record id. */
    id?: string;
    /** Plain URL, used when no record_type/id is given. */
    url?: string;
}

/** One cell of a row: a coloured status pill. */
export interface MatrixCell {
    /**
     * Semantic status. Recognised values (case-insensitive): success / passed / ok,
     * failed / failure / error, warning, cancelled, running, unknown. Anything else
     * renders grey.
     */
    status?: string;
    /** Optional explicit colour token (e.g. `primary-green`); overrides `status`. */
    color?: string;
    /** Optional hover text. Defaults to the capitalised status. */
    tooltip?: string;
    /** Optional link the cell navigates to. */
    link?: MatrixCellLink;
}

/** One row of the matrix. */
export interface MatrixItem {
    /** Row label. */
    label: string;
    /** Optional full text shown on hover over the row label (e.g. an untruncated name). */
    title?: string;
    /** Cells, in display order (typically most-recent first). */
    cells?: MatrixCell[];
}

/** One group of rows. */
export interface MatrixGroup {
    label: string;
    items?: MatrixItem[];
}

/** The whole aggregated matrix, as stored in the SETTING `value`. */
export interface MatrixValue {
    /** ISO timestamp the matrix was generated; shown in the summary line. */
    generated_at?: string;
    /** Optional free-text summary line shown above the groups. */
    summary?: string;
    groups?: MatrixGroup[];
}

/**
 * Semantic status → StatusBadge colour token (named palette tokens only, no
 * ad-hoc hex — per the color-palette design principle). `cancelled` is grey, not
 * red, so a cancellation does not read as a hard failure.
 */
const STATUS_COLORS: Record<string, string> = {
    success: "primary-green",
    passed: "primary-green",
    ok: "primary-green",
    failed: "primary-red",
    failure: "primary-red",
    error: "primary-red",
    warning: "primary-yellow",
    cancelled: "dark-grey",
    running: "primary-blue",
};

const FALLBACK_COLOR = "hover-grey";

/** Colour token for a cell: explicit `color` wins, else the status mapping, else grey. */
export const cellColor = (cell: MatrixCell): string =>
    cell.color || STATUS_COLORS[(cell.status ?? "").toLowerCase()] || FALLBACK_COLOR;

/** Capitalised human label for a status ("Unknown" when missing). */
export const statusLabel = (status?: string): string =>
    status ? status.charAt(0).toUpperCase() + status.slice(1) : "Unknown";

/** Hover text for a cell: its explicit tooltip, else the capitalised status. */
export const cellTooltip = (cell: MatrixCell): string => cell.tooltip || statusLabel(cell.status);

/**
 * Select and order the groups to display. When `configGroups` is given, only
 * those groups are shown, in that order (missing ones skipped); otherwise all
 * groups are shown in their stored order.
 */
export const selectGroups = (value: MatrixValue | null, configGroups?: string[]): MatrixGroup[] => {
    const groups = value?.groups ?? [];
    if (!configGroups || configGroups.length === 0) {
        return groups;
    }
    const byLabel = new Map(groups.map((g) => [g.label, g] as const));
    return configGroups
        .map((label) => byLabel.get(label))
        .filter((g): g is MatrixGroup => Boolean(g));
};
